import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import type { ReactNode } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { useMyOrg } from "@/hooks/use-my-org"
import { listFacilitiesByOrg, type FacilityListItem } from "@/libs/facilityApi"

/**
 * FacilityContext — the multi-facility selection seam (F2 Layer-3).
 *
 * The provider holds the operator's currently-selected facility (id/name/type),
 * mirrors it to localStorage (`bw_selected_facility`), and exposes an imperative
 * API (`setFacility` / `clearFacility`) consumed by the sidebar selector, the org
 * dashboard, and every migrated workspace.
 *
 * `useFacilitySync` (mounted once, in DashboardLayout) reconciles the selection
 * against the org's actual facility list — see the inline behaviors below. It is
 * intentionally separate from the pure-state provider so the provider stays a
 * dependency-free, network-free local store.
 */

const STORAGE_KEY = "bw_selected_facility"

export interface FacilityContextValue {
  facilityId: string | null
  facilityName: string | null
  facilityType: string | null
  setFacility: (id: string, name: string, type?: string | null) => void
  clearFacility: () => void
}

const FacilityContext = createContext<FacilityContextValue>({
  facilityId: null,
  facilityName: null,
  facilityType: null,
  setFacility: () => {},
  clearFacility: () => {},
})

interface StoredSelection {
  id: string | null
  name: string | null
  type: string | null
}

/** Read the persisted selection, tolerating corrupt / partial storage. */
function loadFromStorage(): StoredSelection {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as {
        id?: string
        name?: string
        type?: string | null
      }
      // A valid selection needs BOTH id and name — anything else is "no selection".
      if (parsed.id && parsed.name) {
        return { id: parsed.id, name: parsed.name, type: parsed.type ?? null }
      }
    }
  } catch {
    // Corrupt JSON / unavailable storage → fall through to empty defaults.
  }
  return { id: null, name: null, type: null }
}

export function clearPersistedFacilitySelection() {
  try { localStorage.removeItem(STORAGE_KEY) } catch { /* Storage is optional. */ }
}

export function FacilityProvider({ children }: { children: ReactNode }) {
  const [stored] = useState(loadFromStorage)
  const [facilityId, setFacilityId] = useState<string | null>(stored.id)
  const [facilityName, setFacilityName] = useState<string | null>(stored.name)
  const [facilityType, setFacilityType] = useState<string | null>(stored.type)

  const setFacility = useCallback(
    (id: string, name: string, type?: string | null) => {
      setFacilityId(id)
      setFacilityName(name)
      setFacilityType(type ?? null)
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ id, name, type: type ?? null }),
        )
      } catch {
        // Storage full / unavailable — keep the in-memory selection.
      }
    },
    [],
  )

  const clearFacility = useCallback(() => {
    setFacilityId(null)
    setFacilityName(null)
    setFacilityType(null)
    clearPersistedFacilitySelection()
  }, [])

  const value = useMemo(
    () => ({ facilityId, facilityName, facilityType, setFacility, clearFacility }),
    [facilityId, facilityName, facilityType, setFacility, clearFacility],
  )

  return (
    <FacilityContext.Provider value={value}>{children}</FacilityContext.Provider>
  )
}

export function useSelectedFacility(): FacilityContextValue {
  return useContext(FacilityContext)
}

/** Query the org's facility list, keyed by org id (disabled until known). */
export function useOrgFacilitiesQuery(orgId: string | null | undefined) {
  return useQuery<FacilityListItem[]>({
    queryKey: ["org-facilities", orgId],
    queryFn: () => listFacilitiesByOrg(orgId as string),
    enabled: !!orgId,
  })
}

/**
 * useFacilitySync — reconciles the selected facility against the org's actual
 * facility list. Mounted once inside DashboardLayout (org context present).
 *
 *   - queries ['org-facilities', orgId] (disabled until orgId known)
 *   - clears the selection when there is no org (stale session cleanup)
 *   - clears when the selected id is NOT in the returned list (#883 — a different
 *     user's cached facility must not leak through)
 *   - does NOT clear a still-valid selection on refetch (no flicker / churn)
 *   - auto-selects facilities[0] when none is selected
 *   - primes queryClient.setQueryData(['facility', id], f) for each facility
 */
export function useFacilitySync(): void {
  const { facilityId, setFacility, clearFacility } = useSelectedFacility()
  const queryClient = useQueryClient()

  const orgView = useMyOrg()
  const orgId = orgView.org?.org_id
  const orgResolved = orgView.kind !== "loading"

  const { data: facilities } = useOrgFacilitiesQuery(orgId)

  // Stale-session cleanup: org resolved to "none" but a selection persists.
  useEffect(() => {
    if (orgResolved && !orgId && facilityId) {
      clearFacility()
    }
  }, [orgResolved, orgId, facilityId, clearFacility])

  // Prime the per-facility cache so detail reads (getFacility) resolve instantly
  // from the list we already have. Facility detail is long-lived: keep it cached
  // for 5 minutes rather than dropping it the instant no component observes it.
  useEffect(() => {
    facilities?.forEach((f) => {
      queryClient.setQueryDefaults(["facility", f.facility_id], {
        gcTime: 5 * 60 * 1000,
        staleTime: 60 * 1000,
      })
      queryClient.setQueryData(["facility", f.facility_id], f)
    })
  }, [facilities, queryClient])

  // #883 — clear a selection that is NOT in the current org's list (foreign leak).
  useEffect(() => {
    if (
      facilityId &&
      facilities &&
      !facilities.some((f) => f.facility_id === facilityId)
    ) {
      clearFacility()
    }
  }, [facilityId, facilities, clearFacility])

  // Auto-select the first facility when nothing is selected.
  useEffect(() => {
    if (!facilityId && facilities && facilities.length > 0) {
      const first = facilities[0]
      setFacility(first.facility_id, first.name, first.facility_type)
    }
  }, [facilityId, facilities, setFacility])
}
